import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Input } from '@/components/ui'
import Modal from '@/components/ui/Modal'
import ProcessTemplateSelector from './ProcessTemplateSelector'
import useFormConfirmation from '@/hooks/useFormConfirmation.jsx'
import { createMacroProcess } from '../services/macroProcessService'
import { addFlashMessage } from '../../flash/flashMessagesSlice'
import {
  closeModal as closeModalAction,
  toggleTemplate,
  setTemplatePosition,
  resetRequestStatus,
} from '../macroProcessCreationSlice'

const PURPOSE_TITLES = {
  vente: 'Créer un process de vente',
  location: 'Créer un process de location',
}

/**
 * @typedef {Object} ProcessContainerModalProps
 * @property {number} bienId
 */

/**
 * @param {ProcessContainerModalProps} props
 */
export default function ProcessContainerModal({ bienId }) {
  const dispatch = useDispatch()
  const modalRef = useRef(null)

  const {
    modalOpen,
    activePurpose,
    name: reduxName,
    availableTemplates,
    selectedTemplates,
    loadingStatus,
    requestStatus,
  } = useSelector(state => state.macroProcessCreation)

  const errors = requestStatus?.errors
  const isCreating = loadingStatus.create === 'pending'
  const isLoadingTemplates = loadingStatus.fetchTemplates === 'pending'
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { handleSubmit, reset, getValues, setValue, control, watch, formState: { errors: formErrors } } = useForm({
    defaultValues: { name: reduxName },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const onReset = () => {
    reset()
    dispatch(resetRequestStatus())
  }

  const onClose = () => {
    dispatch(closeModalAction())
  }

  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog,
  } = useFormConfirmation({
    watch,
    getValues,
    onReset,
    onClose,
  })

  // Pilote l'ouverture/fermeture native du <dialog> via l'API impérative
  useEffect(() => {
    const dialog = modalRef.current
    if (!dialog) return
    if (modalOpen) {
      // showModal() requiert que le dialog ne soit pas déjà ouvert (attribut HTML open absent)
      if (!dialog.open) dialog.showModal()
    } else {
      if (dialog.open) dialog.close()
    }
  }, [modalOpen])

  // Synchronise le nom depuis Redux et marque les valeurs initiales à l'ouverture du modal
  useEffect(() => {
    if (modalOpen) {
      setValue('name', reduxName)
      setTimeout(() => {
        setInitialValues(getValues())
        setInitialLoadComplete(true)
        setFormIsDirty(false)
      }, 100)
    }
  }, [modalOpen])

  // Détecte la fin du chargement pour gérer succès / erreur
  useEffect(() => {
    if (isSubmitting && !isCreating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        reset()
      }
    }
  }, [isCreating, errors])

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)
      const action = await dispatch(createMacroProcess({
        bien_id: bienId,
        name: formData.name,
        purpose: activePurpose,
        template_positions: selectedTemplates.map(t => ({
          template_id: t.templateId,
          position: t.position,
        })),
      }))

      if (createMacroProcess.fulfilled.match(action)) {
        dispatch(addFlashMessage({ type: 'success', text: 'Process créé avec succès' }))
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) => {
    const errorsMsg = formErrors?.[fieldName]?.message || errors?.[fieldName] || ''
    if (Array.isArray(errorsMsg)) {
      return errorsMsg.join(', ')
    }
    return errorsMsg
  }

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean}
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  return (
    <Modal
      ref={modalRef}
      backdrop={false}
      onClose={onClose}
      className="max-w-lg"
    >
      <Modal.Header showCloseButton={false} className="mb-4">
        {PURPOSE_TITLES[activePurpose] || 'Créer un process'}
      </Modal.Header>

      <Modal.Body className="overflow-y-auto min-h-[400px]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <Input.Text
            name="name"
            label="Nom du process"
            control={control}
            disabled={isCreating}
            rules={{ required: 'Le nom du process est obligatoire' }}
            validator={{ hasError, getErrorMessage }}
            className="w-full mb-2"
          />
        </div>

        <div className="card bg-base-100 border border-base-200 rounded-none mb-4">
          <div className="p-2 min-h-[400px]">
              <p className="label-text font-medium mb-2">Sélectionner les templates</p>
              <div className="max-h-[calc(80vh-150px)] overflow-y-auto">
                {isLoadingTemplates ? (
                  <div className="flex items-center gap-2 py-4">
                    <span className="loading loading-spinner loading-sm" />
                    <span className="text-sm text-base-content/60">Chargement des templates…</span>
                  </div>
                ) : (
                  <>
                    {hasError('position') && (
                      <p className="text-error text-sm mt-2">{getErrorMessage('position')}</p>
                    )}
                    <ProcessTemplateSelector
                      templates={availableTemplates}
                      isSelected={(id) => selectedTemplates.some(t => t.templateId === id)}
                      getPosition={(id) => {
                        const item = selectedTemplates.find(t => t.templateId === id)
                        return item ? item.position : ''
                      }}
                      onToggle={(id) => dispatch(toggleTemplate(id))}
                      onPositionChange={(id, position) => dispatch(setTemplatePosition({ templateId: id, position }))}
                    />
                  </>
                )}
              </div>
          </div>
        </div>

        <div className="modal-action justify-between">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleFormCancel}
            disabled={isCreating}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isCreating || isLoadingTemplates}
          >
            {isCreating && <span className="loading loading-spinner loading-xs mr-1" />}
            Enregistrer
          </button>
        </div>
      </form>
      </Modal.Body>

      <FormConfirmDialog />
    </Modal>
  )
}
